import { InterventionAcces } from '../intervention-acces/intervention-acces';
import {
  afterRenderEffect,
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
  DestroyRef,
} from '@angular/core';
import { EtatService } from '../../Services/etat';
import type { Etat, Parc } from '../../Services/EntiteProjet';
import { Authentification } from '../../Services/authentification';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { CreerParcDialog } from '../creer-parc-dialog/creer-parc-dialog';
import { statusLabel } from '../../Services/format';

@Component({
  selector: 'app-toolbar',
  imports: [RouterLink, InterventionAcces],
  templateUrl: './toolbar.html',
  styleUrl: './toolbar.css',
  host: {
    '(document:click)': 'fermerListeExterieure($event)',
    '(window:resize)': 'positionnerListe()',
  },
})
export class Toolbar {
  private etat = inject(EtatService);
  private auth = inject(Authentification);
  private router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly selecteur = viewChild<ElementRef<HTMLElement>>('selecteur');
  private readonly listeToggle = viewChild<ElementRef<HTMLButtonElement>>('listeToggle');
  private readonly installationNom = viewChild<ElementRef<HTMLElement>>('installationNom');
  private readonly champRecherche =
    viewChild<ElementRef<HTMLInputElement>>('rechercheInstallation');
  readonly positionListe = signal<{ gauche: number; largeurMax: number } | null>(null);
  readonly creationOuverte = signal(false);

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.fermerListe());
    afterRenderEffect({
      write: () => this.champRecherche()?.nativeElement.focus(),
      read: () => {
        this.installationSelectionnee();
        this.positionnerListe();
      },
    });
  }

  ouvrirCreationParc(): void {
    if (this.creationOuverte()) return;
    this.creationOuverte.set(true);
    this.listeOuverte.set(false);
    const ref = this.dialog.open(CreerParcDialog, {
      width: '600px',
      maxWidth: 'calc(100vw - 24px)',
      ariaLabelledBy: 'creer-parc-titre',
      ariaDescribedBy: 'creer-parc-description',
      autoFocus: '#parc-libelle',
      restoreFocus: '.ghost-icon',
    });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((parc: Parc | undefined) => {
        this.creationOuverte.set(false);
        if (parc) void this.router.navigate(['/parc']);
      });
  }

  private readonly routeCourante = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) route = route.firstChild;
        return route;
      }),
    ),
    { requireSync: true },
  );
  readonly afficherParc = computed(() => !this.routeCourante().data['hideSiloParc']);
  readonly installationId = computed(() => {
    const route = this.routeCourante();
    if (!route.routeConfig?.path?.startsWith('installation/')) return null;
    return Number(route.paramMap.get('id'));
  });
  readonly installationSelectionnee = computed(() => {
    const id = this.installationId();
    if (id === null) return null;
    const installation = this.etat.installationCourante();
    return installation?.id === id
      ? installation
      : (this.etat.installations().find((candidate) => candidate.id === id) ?? null);
  });
  readonly installations = computed(() =>
    [...this.etat.installations()].sort(
      (a, b) =>
        this.rangCriticite(a.etat) - this.rangCriticite(b.etat) ||
        a.nom.localeCompare(b.nom, 'fr') ||
        a.id - b.id,
    ),
  );
  readonly rechercheInstallations = signal('');
  readonly installationsFiltrees = computed(() => {
    const recherche = this.normaliserRecherche(this.rechercheInstallations());
    return this.installations().filter(
      (installation) =>
        this.normaliserRecherche(installation.nom).includes(recherche) ||
        String(installation.id).includes(recherche),
    );
  });
  readonly statusLabel = statusLabel;
  parcs = this.etat.parcs;
  parcSelectionne = this.etat.parcSelectionne;
  nombreInterventionsAVenir = this.etat.nombreInterventionsAVenir;
  utilisateur = this.auth.tokenInfoSignal;
  estAdmin = computed(() => this.utilisateur()?.roles.includes('ROLE_ADMIN') ?? false);
  listeOuverte = signal(false);
  nomUtilisateur = computed(() => {
    const utilisateur = this.utilisateur();
    if (!utilisateur) return 'Utilisateur';
    const nomComplet = [utilisateur.prenom, utilisateur.nom].filter(Boolean).join(' ').trim();
    return nomComplet || utilisateur.username;
  });
  initialeUtilisateur = computed(() => this.nomUtilisateur().trim().charAt(0).toUpperCase() || 'U');
  tooltipInterventions = computed(() => {
    const nombre = this.nombreInterventionsAVenir();
    const pluriel = nombre > 1 ? 's' : '';
    return `${nombre} intervention${pluriel} planifiée${pluriel} dans le parc sélectionné.`;
  });

  toggleListe() {
    if (this.listeOuverte()) {
      this.fermerListe();
    } else {
      this.rechercheInstallations.set('');
      this.listeOuverte.set(true);
    }
  }

  effacerRechercheInstallations(): void {
    this.rechercheInstallations.set('');
    this.champRecherche()?.nativeElement.focus();
  }

  /** Arrêt prioritaire même hors communication ; états absents après les sites sans ticket. */
  private rangCriticite(etat?: Etat | null): number {
    if (!etat) return Infinity;
    const niveau = etat.arret ? EtatService.NIVEAU_ARRET : EtatService.calculerNiveau(etat);
    return EtatService.rangNiveau(niveau);
  }

  /** Recherche locale par nom ou identifiant, insensible à la casse et aux accents. */
  private normaliserRecherche(valeur: string): string {
    return valeur
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLocaleLowerCase('fr');
  }

  positionnerListe(): void {
    if (!this.listeOuverte()) return;
    const nom = this.installationNom()?.nativeElement;
    const selecteur = this.selecteur()?.nativeElement;
    if (!nom || !selecteur || window.innerWidth <= 640) {
      this.positionListe.set(null);
      return;
    }
    // Le menu suit le libellé, quelle que soit la largeur du nom du parc.
    const gauche = nom.getBoundingClientRect().left;
    this.positionListe.set({
      gauche: gauche - selecteur.getBoundingClientRect().left,
      largeurMax: Math.max(
        0,
        Math.min(480, nom.ownerDocument.documentElement.clientWidth - gauche - 12),
      ),
    });
  }

  fermerListe(restaurerFocus = false): void {
    if (!this.listeOuverte()) return;
    this.listeOuverte.set(false);
    if (restaurerFocus) this.listeToggle()?.nativeElement.focus();
  }

  fermerListeExterieure(event: MouseEvent): void {
    if (event.target instanceof Node && !this.selecteur()?.nativeElement.contains(event.target)) {
      this.fermerListe();
    }
  }

  lienInstallation(id: number): (string | number)[] {
    const rubrique = this.routeCourante()
      .url.slice(2)
      .map((segment) => segment.path);
    return ['/installation', id, ...rubrique];
  }

  /** Rétablit le périmètre global avant de naviguer, même si la liste n’est pas encore chargée. */
  async selectionnerParcGlobal(): Promise<void> {
    const session = this.auth.sessionVersion;
    this.fermerListe();
    if (!this.parcs().some((parc) => parc.global)) await this.etat.chargerParcs();
    // Une réponse tardive ne doit pas rediriger une nouvelle session.
    if (this.destroyRef.destroyed || session !== this.auth.sessionVersion) return;
    const parcGlobal = this.parcs().find((parc) => parc.global);
    if (!parcGlobal) return;
    this.etat.parcSelectionne.set(parcGlobal);
    await this.router.navigate(['/parc']);
  }

  selectionner(parc: Parc) {
    this.etat.parcSelectionne.set(parc);
    this.listeOuverte.set(false);
    void this.router.navigate(['/parc']);
  }

  seDeconnecter() {
    this.auth.logOut();
  }
}
